import API from "serviceshift-ui/api-client";
import { reportRumError } from "@/lib/datadog";

export const GENERIC_ERROR_MESSAGE =
  "An unexpected error occurred, please try again.";

// Mute specific error messages as flash
const mutedErrorMessages = [
  "Email Address Is Already Taken",
  "not_covered_error"
];

// An unhydrated frontend entity serializes its `id = -1` default (or a literal
// `undefined`) into the URL, producing backend RecordNotFound 404s that lock up
// the UI. Block those requests before they leave the browser and report them so
// the originating flow is visible in Sentry. Scoped to quote/customer/user
// resources so the scheduler's legitimate `-1` "unassigned" sentinel is untouched.
const SENTINEL_ID_PATH =
  /\/(?:quotes|customers|users)\/(?:-1|undefined)(?:[/?]|$)/;
const SENTINEL_ID_QUERY =
  /[?&](?:customer_id|user_id|quote_id)=(?:-1|undefined)(?:&|$)/;

export const hasSentinelId = (url) =>
  !!url && (SENTINEL_ID_PATH.test(url) || SENTINEL_ID_QUERY.test(url));

const reportSentinelRequest = (url, method) => {
  const label = `${(method || "get").toUpperCase()} ${url}`;

  console.error(`Blocked API request with sentinel id: ${label}`);
  window.Sentry?.withScope((scope) => {
    scope.setTag("sentinel_id_request", "true");
    scope.setExtra("request_url", url);
    scope.setExtra("request_method", method);
    window.Sentry.captureException(
      new Error(`Blocked API request with sentinel id: ${label}`)
    );
  });
};

// Surface an API failure in Datadog RUM. These are the paths where the user
// sees a generic "unexpected error" (or a silent no-response failure) with no
// server-side exception to trace — instrumenting them makes an otherwise
// invisible failure findable. Path only (no query string) to avoid leaking ids.
// Exported for the callers that opt out of the generic branch below with
// ignoreError: suppressing the flash must not also suppress the RUM report, or
// a message-less failure becomes invisible.
export const reportApiFailure = (error, reason) => {
  reportRumError("API request failed", {
    reason,
    requestMethod: (error.config?.method || "").toUpperCase(),
    requestUrl: (error.config?.url || "").split("?")[0],
    status: error.response?.status ?? null
  });
};

export const responseInterceptor = (error) => {
  if (error.response) {
    if (error.response.status === 401) {
      // Skip redirect for login form requests
      if (error.config.headers.skipRedirectOn401) {
        return Promise.reject(error);
      }
      // Slight delay so UI has a chance to
      // display error message before redirect
      setTimeout(() => {
        window.location.href = window.location.origin;
      }, 500);
      return Promise.reject(error);
    }
    if (
      error.response.data?.includes &&
      error.response.data?.includes("Can't verify CSRF token")
    ) {
      reportApiFailure(error, "csrf_reload");
      setTimeout(() => {
        window.location.reload();
      }, 500);
      window.notify.error(GENERIC_ERROR_MESSAGE);
      return Promise.reject(error);
    }
    if (
      !mutedErrorMessages.includes(error.response.data.message) &&
      !error.config.headers.ignoreError
    ) {
      const serverMessage = error.response.data.message;
      if (!serverMessage) {
        reportApiFailure(error, "generic_error");
      }
      window.notify.error(serverMessage || GENERIC_ERROR_MESSAGE);
    }
  } else {
    // No response at all — network error, timeout, or a blocked request. This
    // reaches no server logs, so RUM is the only place it becomes visible.
    reportApiFailure(error, "no_response");
  }
  return Promise.reject(error);
};

export const requestInterceptor = (config) => {
  if (hasSentinelId(config.url)) {
    reportSentinelRequest(config.url, config.method);
    return Promise.reject(
      new Error(`Blocked API request with sentinel id: ${config.url}`)
    );
  }

  if (config.headers["acceptHTML"]) return config;

  const isFormData = config.data instanceof FormData;

  if (!isFormData) {
    config.headers["Accept"] = "application/json";
    config.headers["Content-Type"] = "application/json";
  }

  return config;
};

API.ejectResponseInterceptor();
API.attachResponseInterceptor(responseInterceptor);
API.ejectRequestInterceptor();
API.attachRequestInterceptor(requestInterceptor);
